import { Box, NavLink, rem, Text, Tooltip } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
	type AdminNavGroup,
	type AdminNavItem,
	isNavGroup,
	isNavGroupActive,
	isNavPathActive,
} from "./admin-nav.logic";

interface Props {
	item: AdminNavItem;
	pathname: string;
	tooltipEnabled: boolean;
	onNavigate: (to: string) => void;
}

const ROOT_STYLES = {
	root: { borderRadius: rem(8), marginBottom: rem(4) },
	label: { fontSize: rem(14) },
};

const ICON_SIZE = { width: rem(20), height: rem(20) };
const CHILD_ICON_SIZE = { width: rem(18), height: rem(18) };

/** Satu entri sidebar admin: menu biasa, atau grup yang bisa dilipat berisi sub menu. */
export function AdminNavEntry({
	item,
	pathname,
	tooltipEnabled,
	onNavigate,
}: Props) {
	if (isNavGroup(item)) {
		return <NavGroup item={item} pathname={pathname} onNavigate={onNavigate} />;
	}
	const active = isNavPathActive(pathname, item.to);
	return (
		<Tooltip
			label={item.description}
			position="right"
			disabled={!tooltipEnabled}
			openDelay={500}
		>
			<NavLink
				onClick={() => onNavigate(item.to)}
				leftSection={<item.icon style={ICON_SIZE} stroke={1.5} />}
				label={
					<Box>
						<Text size="sm" fw={active ? 700 : 500}>
							{item.label}
						</Text>
					</Box>
				}
				rightSection={<IconChevronRight size="0.8rem" stroke={1.5} />}
				active={active}
				variant="light"
				color="orange"
				styles={ROOT_STYLES}
			/>
		</Tooltip>
	);
}

function NavGroup({
	item,
	pathname,
	onNavigate,
}: {
	item: AdminNavGroup;
	pathname: string;
	onNavigate: (to: string) => void;
}) {
	const groupActive = isNavGroupActive(pathname, item);
	const [opened, setOpened] = useState(groupActive);

	// Buka otomatis saat user masuk ke salah satu anak lewat tautan di luar sidebar.
	useEffect(() => {
		if (groupActive) setOpened(true);
	}, [groupActive]);

	return (
		<NavLink
			label={
				<Text size="sm" fw={groupActive ? 700 : 500}>
					{item.label}
				</Text>
			}
			leftSection={<item.icon style={ICON_SIZE} stroke={1.5} />}
			opened={opened}
			onChange={setOpened}
			active={groupActive}
			variant="light"
			color="orange"
			styles={ROOT_STYLES}
		>
			{item.children.map((child) => (
				<NavLink
					key={child.to}
					onClick={() => onNavigate(child.to)}
					leftSection={<child.icon style={CHILD_ICON_SIZE} stroke={1.5} />}
					label={child.label}
					active={isNavPathActive(pathname, child.to)}
					variant="light"
					color="orange"
					styles={ROOT_STYLES}
				/>
			))}
		</NavLink>
	);
}
